import type { DateOrRange } from '@/infrastructure/departure-date/store'
import type { DepartureDateFilterOptions } from '@/scopes/search/components/DepartureDateFilter/types'
import type { IconGlyph } from '@sunwebgroup/ui-components/uds'
import { useDepartureDateStore } from '@/infrastructure/departure-date/useDepartureDateStore'
import { useIntl } from '@/intl'
import { DatesCalendar, MonthCalendar } from '@/scopes/search/components/DepartureDateFilter/Calendars'
import { useDepartureDateFilter } from '@/scopes/search/components/DepartureDateFilter/hooks'
import Tabs from '@/scopes/search/components/DepartureDateFilter/Tabs'
import { Toggle } from '@/scopes/search/components/DepartureDateFilter/Toggle'
import { Filter } from '@/scopes/search/components/QuickSearchBar/Filter'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import { mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, IndeterminateProgressCircle, Popover } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

const FilterPopoverContent = styled(Filter)`
  && {
    @media (min-width: ${tokPx('primitives_breakpoint_s_max')}) {
      width: 100%;
    }
  }

  ${Popover.Content.modWithPadding(false)}
`

function DepartureDateFilter({
  name,
  options,
  className,
}: {
  name: string
  options: DepartureDateFilterOptions
  className?: string
}) {
  const { view } = useDepartureDateFilter({ options })
  if (view === 'calendar') {
    return (<div className={className}><DatesCalendar options={options} /></div>)
  }

  return (<div className={className}><MonthCalendar name={name} options={options} /></div>)
}

const MobileDepartureDateFilter = styled(DepartureDateFilter)`
  padding: ${space('primitives_space_16')};
  width: 100%;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    margin-bottom: 0;
    margin-top: 0;
    width: auto;
  }
`

const DesktopDepartureDateFilter = styled(DepartureDateFilter)``

const FilterWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${space('primitives_space_16')};

  ${DesktopDepartureDateFilter} {
    display: none;
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${DesktopDepartureDateFilter} {
      display: initial;
    }

    ${MobileDepartureDateFilter} {
      display: none;
    }
  }
`
const PopoverHeader = styled(Tabs)`
  padding: ${space('primitives_space_16')};
  background: ${tok('semantics_views_background_color')};
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 99;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    position: initial;
    top: initial;
    width: ${tokPx('primitives_area_size_360')};
    background: initial;
    z-index: initial;
  }
`

const FooterButtonWrapper = styled.div`
  display: flex;
  gap: ${space('primitives_space_8')};
  margin-left: auto;
  background-color: ${tok('semantics_views_background_color')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    background-color: initial;
  }

  button {
    width: 100%;

    &:enabled {
      span {
        margin-left: unset;
      }
    }
  }
`

const Footer = styled.div`
  bottom: 0;
  display: flex;
  flex-direction: column;
  position: -webkit-sticky;
  position: sticky;
  width: 100%;
  z-index: 1;

  ${FooterButtonWrapper} {
    border-top: 1px solid ${tok('semantics_dividers_color')};
    width: 100%;
  }

  ${Toggle}, ${FooterButtonWrapper} {
    padding: ${space('primitives_space_16')};
  }

  ${Toggle} {
    overflow-x: auto;
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    flex-direction: row;

    ${FooterButtonWrapper} {
      border-top: initial;
      width: initial;
    }
  }
`

export function DepartureDateFilterComponent({
  disabled,
  onConfirm,
  onReset,
  options,
  id,
  icon,
}: {
  onConfirm: ({ departureDate }: { departureDate: DateOrRange }) => void
  onReset: () => void
  options: DepartureDateFilterOptions
  disabled?: boolean
  id: string
  icon?: IconGlyph
}) {
  const intl = useIntl()
  const { setActiveItem } = useQuickSearchStore()
  const { view, departureDate, reset } = useDepartureDateStore()
  const { formattedText } = useDepartureDateFilter({ options })

  const handleConfirm = () => {
    setActiveItem(null)

    if (disabled)
      return

    if (departureDate) {
      onConfirm({ departureDate })
    }
  }

  const handleReset = () => {
    onReset()
    reset()
    setActiveItem(null)
  }

  return (
    <FilterPopoverContent
      id={id}
      icon={icon}
      placeholder={intl.formatMessage({
        id: 'quicksearch_filter_departure_date_placeholder',
      })}
      title={intl.formatMessage({
        id: 'quicksearch_filter_departure_date_title',
      })}
      onConfirm={handleConfirm}
      position="bottom-right"
      selectionFormattedText={formattedText?.long}
      compactSelectionFormattedText={formattedText?.short || formattedText?.long}
    >
      <PopoverHeader options={options} />
      <FilterWrapper>
        <MobileDepartureDateFilter options={options} name="mobile-departure-filter" />
        <DesktopDepartureDateFilter options={options} name="desktop-departure-filter" />
      </FilterWrapper>
      <Footer>
        {view === 'calendar' && <Toggle options={options} />}
        <FooterButtonWrapper>
          <Button onClick={handleReset} $type="transparentPrimary" disabled={disabled}>
            {intl.formatMessage({
              id: 'quicksearch_filter_departure_date_reset',
            })}
          </Button>
          <Button onClick={handleConfirm} disabled={disabled}>
            {disabled && <IndeterminateProgressCircle />}
            {intl.formatMessage({
              id: 'quicksearch_save_button_text',
            })}
          </Button>
        </FooterButtonWrapper>
      </Footer>
    </FilterPopoverContent>
  )
}
